import { useState, useEffect } from 'react'
import { useNavigate } from 'react-router-dom'
import api from '../../utils/api'
import { useAuth } from '../../hooks/useAuth'
import { usePermissions } from '../../hooks/usePermissions'
import { printCaseFromDb } from '../../utils/printCasePdf'
import Button from '../ui/Button'
import Alert from '../ui/Alert'
import ar from '../../locales/ar.json'
import { Edit2, X, Check } from 'lucide-react'

const t = ar.admin

const STATUS_FLOW = ['draft', 'submitted', 'pending review', 'documented', 'rejected']
const STATUS_LABELS = {
  draft:            'مسودة',
  submitted:        'مقدَّم',
  'pending review': 'قيد المراجعة',
  documented:       'موثَّق',
  rejected:         'مرفوض',
}
const STATUS_BADGE = {
  draft:            'badge-stone',
  submitted:        'badge-blue',
  'pending review': 'badge-yellow',
  documented:       'badge-green',
  rejected:         'badge-red',
}

function Row({ label, value }) {
  if (!value && value !== 0 && value !== false) return null
  const display = typeof value === 'boolean' ? (value ? 'نعم' : 'لا')
                : Array.isArray(value) ? value.join('، ')
                : String(value)
  return (
    <div className="py-1.5 grid grid-cols-3 gap-2 border-b border-stone-100 last:border-0">
      <dt className="text-stone-500 text-sm">{label}</dt>
      <dd className="col-span-2 text-stone-800 text-sm font-medium">{display || '—'}</dd>
    </div>
  )
}

function Section({ title, children }) {
  return (
    <div className="card mb-4">
      <h3 className="font-semibold text-primary-700 mb-3 pb-2 border-b border-stone-100">{title}</h3>
      <dl>{children}</dl>
    </div>
  )
}

// ─── Editable fields config ─────────────────────────────────────────────────
const EDIT_FIELDS = [
  { key: 'martyr_fullname',      label: 'اسم الشهيد/المفقود',   type: 'text' },
  { key: 'mother_name',          label: 'اسم الأم',             type: 'text' },
  { key: 'birth_date',           label: 'تاريخ الميلاد',        type: 'date' },
  { key: 'current_residence',    label: 'مكان الإقامة',         type: 'text' },
  { key: 'civil_registry_place', label: 'مكان النفوس',          type: 'text' },
  { key: 'marital_status',       label: 'الحالة الاجتماعية',    type: 'text' },
  { key: 'incident_type',        label: 'نوع الحادثة',          type: 'text' },
  { key: 'region',               label: 'المنطقة',              type: 'text' },
  { key: 'neighborhood',         label: 'الحي',                  type: 'text' },
  { key: 'submitter_notes',      label: 'ملاحظات',              type: 'textarea' },
]

export default function CaseDetail({ caseId }) {
  const { user }         = useAuth()
  const { can }          = usePermissions()
  const navigate         = useNavigate()
  const [caseData, setCaseData] = useState(null)
  const [loading, setLoading]   = useState(true)
  const [error,   setError]     = useState('')
  const [saving,  setSaving]    = useState(false)
  const [statusNote, setStatusNote] = useState('')
  const [newStatus, setNewStatus]   = useState('')
  const [auditLog,  setAuditLog]    = useState([])
  const [editing,   setEditing]     = useState(false)
  const [editForm,  setEditForm]    = useState({})
  const [editSaving, setEditSaving] = useState(false)
  const [editError,  setEditError]  = useState('')

  useEffect(() => {
    if (!caseId) return
    setLoading(true)
    Promise.all([
      api.get(`admin/cases/${caseId}`),
      api.get(`admin/cases/${caseId}/audit`),
    ])
      .then(([res, audit]) => {
        setCaseData(res.data)
        setAuditLog(audit.data || [])
        setNewStatus(res.data.status)
      })
      .catch((err) => setError(err.message || t.errorLoading))
      .finally(() => setLoading(false))
  }, [caseId])

  async function handleStatusChange() {
    if (!newStatus || newStatus === caseData?.status) return
    setSaving(true)
    setError('')
    try {
      const { data } = await api.patch(`admin/cases/${caseId}/status`, {
        status: newStatus,
        note:   statusNote,
      })
      setCaseData((prev) => ({ ...prev, status: data.status }))
      setAuditLog((prev) => [data.audit, ...prev])
      setStatusNote('')
    } catch (err) {
      setError(err.message || t.errorSaving)
    } finally {
      setSaving(false)
    }
  }

  async function handleDelete() {
    if (!window.confirm(t.confirmDelete)) return
    try {
      await api.delete(`admin/cases/${caseId}`)
      navigate('/admin')
    } catch (err) {
      setError(err.message)
    }
  }

  const canChangeStatus = can('can_change_status')
  const canEditFields   = can('can_edit_fields')

  function startEdit() {
    const c = caseData
    setEditForm(Object.fromEntries(EDIT_FIELDS.map(f => [f.key, c[f.key] ?? ''])))
    setEditError('')
    setEditing(true)
  }

  async function saveEdit() {
    setEditSaving(true)
    setEditError('')
    try {
      await api.patch(`admin/cases/${caseId}`, editForm)
      // Refresh case data
      const { data } = await api.get(`admin/cases/${caseId}`)
      setCaseData(data)
      // Refresh audit
      const { data: audit } = await api.get(`admin/cases/${caseId}/audit`)
      setAuditLog(audit || [])
      setEditing(false)
    } catch (err) {
      setEditError(err.message || 'فشل حفظ التعديلات')
    } finally {
      setEditSaving(false)
    }
  }

  if (loading) return <div className="card text-center py-12 text-stone-400">{ar.app.loading}</div>
  if (error)   return <Alert type="error" message={error} />
  if (!caseData) return null

  const c = caseData

  return (
    <div className="max-w-3xl">
      {/* Header */}
      <div className="flex items-center justify-between mb-4 flex-wrap gap-3">
        <div>
          <h2 className="text-lg font-bold text-stone-800">{c.martyr_fullname || '—'}</h2>
          <p className="text-xs text-stone-400 font-mono mt-0.5">{c.readable_id}</p>
        </div>
        <div className="flex items-center gap-3">
          <span className={`badge ${STATUS_BADGE[c.status]}`}>{STATUS_LABELS[c.status]}</span>
          <Button
            variant="ghost"
            size="sm"
            onClick={() => printCaseFromDb(caseData)}
          >
            📄 PDF
          </Button>
          {user?.role === 'admin' && (
            <Button variant="danger" size="sm" onClick={handleDelete}>{t.deleteCase}</Button>
          )}
          {canEditFields && !editing && (
            <Button variant="secondary" size="sm" onClick={startEdit}>
              <Edit2 size={13} className="mr-1" /> تعديل الحقول
            </Button>
          )}
        </div>
      </div>

      {error && <Alert type="error" message={error} className="mb-4" />}

      {/* Status change */}
      {canChangeStatus && (
        <div className="card mb-4">
          <h3 className="font-semibold text-primary-700 mb-3">{t.changeStatus}</h3>
          <div className="flex flex-wrap gap-3">
            <select
              value={newStatus}
              onChange={(e) => setNewStatus(e.target.value)}
              className="input-base flex-1 min-w-[160px]"
              dir="rtl"
            >
              {STATUS_FLOW.map((s) => (
                <option key={s} value={s}>{STATUS_LABELS[s]}</option>
              ))}
            </select>
            <input
              type="text"
              value={statusNote}
              onChange={(e) => setStatusNote(e.target.value)}
              placeholder={t.statusNote}
              className="input-base flex-1 min-w-[160px]"
              dir="rtl"
            />
            <Button
              variant="primary"
              size="sm"
              onClick={handleStatusChange}
              disabled={newStatus === caseData.status || saving}
              loading={saving}
            >
              {t.save}
            </Button>
          </div>
        </div>
      )}

      {/* Case data */}
      {editing && (
        <div className="card mb-4 border-amber-200 bg-amber-50">
          <div className="flex items-center justify-between mb-4">
            <h3 className="font-semibold text-amber-800 flex items-center gap-2">
              <Edit2 size={15} /> تعديل بيانات الحالة
            </h3>
            <button onClick={() => setEditing(false)} className="text-stone-400 hover:text-stone-700">
              <X size={18} />
            </button>
          </div>
          {editError && <Alert type="error" message={editError} className="mb-3" />}
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
            {EDIT_FIELDS.map(f => (
              <div key={f.key} className={f.type === 'textarea' ? 'sm:col-span-2' : ''}>
                <label className="block text-xs font-semibold text-stone-600 mb-1">{f.label}</label>
                {f.type === 'textarea' ? (
                  <textarea
                    value={editForm[f.key] || ''}
                    onChange={e => setEditForm(p => ({ ...p, [f.key]: e.target.value }))}
                    rows={3}
                    className="w-full border border-stone-200 rounded-lg px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-amber-400 bg-white"
                  />
                ) : (
                  <input
                    type={f.type}
                    value={editForm[f.key] || ''}
                    onChange={e => setEditForm(p => ({ ...p, [f.key]: e.target.value }))}
                    className="w-full border border-stone-200 rounded-lg px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-amber-400 bg-white"
                  />
                )}
              </div>
            ))}
          </div>
          <div className="flex justify-end gap-3 mt-4">
            <button onClick={() => setEditing(false)} className="text-sm text-stone-500 hover:text-stone-800 px-4 py-2">إلغاء</button>
            <Button onClick={saveEdit} loading={editSaving}>
              <Check size={14} className="mr-1" /> حفظ
            </Button>
          </div>
        </div>
      )}

      {/* Case data */}
      <Section title="مقدم الطلب">
        <Row label="الاسم"       value={c.name_applicant} />
        <Row label="الصلة"       value={c.relation_applicant} />
        <Row label="الهاتف"      value={c.phone_primary} />
        <Row label="المنطقة"     value={c.area_applicant} />
      </Section>

      <Section title="بيانات الشهيد">
        <Row label="الاسم الكامل" value={c.martyr_fullname} />
        <Row label="اسم الأم"    value={c.mother_name} />
        <Row label="تاريخ الميلاد" value={c.birth_date} />
        <Row label="محل الإقامة" value={c.current_residence} />
        <Row label="مكان النفوس" value={c.civil_registry_place} />
        <Row label="الحالة الاجتماعية" value={c.marital_status} />
      </Section>

      <Section title="تفاصيل الحادثة">
        <Row label="نوع الحادثة" value={c.incident_type} />
        {c.incident_details && Object.entries(c.incident_details).map(([k, v]) => (
          <Row key={k} label={k} value={v} />
        ))}
        <Row label="أنواع الأدلة" value={c.evidence_types} />
      </Section>

      {(c.witnesses?.length > 0) && (
        <Section title="الشهود">
          {c.witnesses.map((w, i) => (
            <div key={i} className="py-1 text-sm border-b border-stone-100 last:border-0">
              <span className="text-stone-500 ml-2">شاهد {i + 1}:</span>
              <span className="text-stone-800">{w.witness_name} — {w.witness_role}</span>
            </div>
          ))}
        </Section>
      )}

      <Section title="الأسرة والتواصل">
        <Row label="المنطقة"    value={c.region} />
        <Row label="الحي"       value={c.neighborhood} />
        <Row label="جهة التواصل" value={c.case_contact_person} />
        <Row label="الهاتف"     value={c.contact_primary_phone} />
      </Section>

      {/* Files */}
      {c.files?.length > 0 && (
        <div className="card mb-4">
          <h3 className="font-semibold text-primary-700 mb-3">{t.attachments}</h3>
          <ul className="space-y-2">
            {c.files.map((f) => (
              <li key={f.id} className="flex items-center gap-3 text-sm">
                <span className="text-stone-500 text-xs">{f.field_name}</span>
                <a
                  href={`/api/v1/admin/files/${f.id}`}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="text-primary-600 hover:text-primary-800 font-medium"
                >
                  {f.original_name}
                </a>
                <span className="text-stone-300 text-xs">
                  {f.size_kb ? `${Math.round(f.size_kb)}KB` : ''}
                </span>
              </li>
            ))}
          </ul>
        </div>
      )}

      {/* Audit log */}
      {auditLog.length > 0 && (
        <div className="card">
          <h3 className="font-semibold text-primary-700 mb-3">{t.auditLog}</h3>
          <ul className="space-y-2 text-sm">
            {auditLog.map((entry, i) => (
              <li key={i} className="flex gap-3 text-stone-600 border-b border-stone-100 pb-2 last:border-0">
                <span className="text-stone-400 text-xs w-28 flex-shrink-0">
                  {new Date(entry.created_at).toLocaleString('ar-SY')}
                </span>
                <span className="flex-1">
                  <span className="font-medium">{entry.actor_username}</span>
                  {' → '}{entry.action}
                  {entry.note && <span className="text-stone-400 text-xs mr-1">({entry.note})</span>}
                </span>
              </li>
            ))}
          </ul>
        </div>
      )}
    </div>
  )
}
